import { useMutation } from '@apollo/client'
import { Button, Col, Form, InputNumber, message, Row, Spin } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoginModal } from '../../../../../context/loginModalContext'
import { showAuthError } from '../../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../../common/CommonStyles'
import { v4 as uuidv4 } from 'uuid'
import { useForceUpdate } from '../../../../../shared/hooks/useForceUpdate'
import CreateNewItemExtraButtons from '../../../../common/CreateNewItemExtraButtons'
import { useNavigate } from 'react-router-dom'
import NameField from '../../../../common/formFields/NameField'
import DeleteRejectReasonButton from './DeleteRejectReasonButton'
import ADD_NEW_REJECT_REASON from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/leads/addNewRejectReason'
import EDIT_REJECT_REASON from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/leads/editRejectReason'

const RejectReasonForm = props => {
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields } = form

  useEffect(() => {
    if (formVisible) {
      resetFields()
    }
  }, [formVisible])

  const [addNewItem, { loading }] = useMutation(ADD_NEW_REJECT_REASON)
  const [editItem, { loading: loadingEdit }] = useMutation(EDIT_REJECT_REASON)

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          let item

          if (selectedItem.new) {
            item = await addNewItem({
              variables: {
                ...values,
                generatedID
              }
            })

            if (key === 'view') {
              navigate(`/settings/leads/reject/${generatedID}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
            } else {
              handleClose(item.data.addNewRejectReason)
            }
          } else {
            item = await editItem({
              variables: {
                ...values,
                rejectReason: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)
          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (item.data.addNewRejectReason && item.data.addNewRejectReason.id) ||
                (item.data.editRejectReason && item.data.editRejectReason.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  return (
    <StyledDrawer
      title={!selectedItem.new ? t('common.edit') : t('patients.newItem')}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new ? (
            <DeleteRejectReasonButton
              selectedItem={selectedItem}
              handleAnimateRow={props.handleAnimateRow}
              handleClose={handleClose}
            />
          ) : selectedItem.new && selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : null}
          <Button loading={loading || loadingEdit} htmlType="submit" type="primary" form={`${generatedID}rejectReasonForm`}>
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}rejectReasonForm`}
          onFinish={handleSubmit}
          initialValues={selectedItem}
        >
          <Row type="flex" gutter={16}>
            <Col span={16}>
              <NameField required min={1} max={100} autoFocus autoComplete="" />
            </Col>
            <Col span={8}>
              <Form.Item name="order" label={t('app.order')}>
                <InputNumber min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

RejectReasonForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func
}

export default RejectReasonForm
